import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Icon } from "iconsax-react";
import { DocumentText1, Edit2, Edit, User, Calendar, MessageText1 } from "iconsax-react";
import { ModalForm, Button, Prompt, Toast, Select, Skeleton } from "@/shared/ui";
import { DetailFieldRow } from "@/features/hr/shared";
import { SigningStatus } from "@/shared/components";
import { CheckIcon, CloseIcon } from "@/shared/assets/icons";
import { formatDateForDisplay, type Locale } from "@/shared/utils";
import { useAuth } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts";
import { usePreviewJobApplicationMutation, useSignJobApplicationMutation, useGetJobApplicationQuery } from "@/features/hr/contracts/api";
import { JOB_APP_PENDING } from "@/features/hr/contracts/consts";
import { getJobApplicationActions } from "@/features/hr/contracts/utils";
import { useOrderSigning, RevokeSigningButton } from "@/features/signing";
import type { SigningStatus as SigningStatusValue } from "@/shared/types";
import type { JobApplication } from "@/features/hr/contracts/types";
import type { HiringStage } from "@/features/hr/hiring/types/stages";

interface Props {
  candidateName: string;
  candidateId: number | null;
  applicationId?: number | null;
  candidateStage?: string | null;
  jobApplication?: JobApplication | null;
  jobApplicationSignedPdfUrl?: string | null;
  onClose: () => void;
  onReview?: (
    action: "approve" | "reject" | "revision",
    approval_resolution?: "approved" | "supported" | "no_objection" | "rejected",
    note?: string,
  ) => void;
  onPreview?: () => void | Promise<void>;
  onSignSuccess?: () => void;
  isReviewing?: boolean;
  isUploading?: boolean;
  isPreviewing?: boolean;
  isCounterSigning?: boolean;
  signingStatusOverride?: SigningStatusValue | null;
  locale?: Locale;
}

export default function JobApplicationDetailsModal({
  candidateName,
  candidateId,
  applicationId,
  candidateStage,
  jobApplication: jobApplicationProp,
  jobApplicationSignedPdfUrl: jobApplicationSignedPdfUrlProp,
  onClose,
  onReview,
  onPreview: _onPreview,
  onSignSuccess,
  isReviewing = false,
  isUploading: _isUploading = false,
  isPreviewing = false,
  isCounterSigning = false,
  signingStatusOverride,
  locale = "ru",
}: Props) {
  const { t } = useTranslation("ContractsPage");
  const { t: tCommon } = useTranslation("Common");
  const { hasPermission, hasRole, user } = useAuth();

  const shouldFetch = !jobApplicationProp && candidateId != null;
  const { data: fetchedJobApplication, isLoading: isFetchingJobApp } = useGetJobApplicationQuery(candidateId!, { skip: !shouldFetch });
  const electronic = fetchedJobApplication?.job_application ?? null;
  const jobApplicationSignedPdfUrl = jobApplicationSignedPdfUrlProp ?? null;

  const [prompt, setPrompt] = useState<{ title: string; text: string; variant?: "success" | "error" } | null>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approvalResolution, setApprovalResolution] = useState<"approved" | "supported" | "no_objection" | null>(null);
  const [reviewNote, setReviewNote] = useState<string>("");

  const [previewJobApp] = usePreviewJobApplicationMutation();
  const [signJobApp] = useSignJobApplicationMutation();

  const jobAppSigning = useOrderSigning({
    previewFn() {
      return previewJobApp(candidateId!)
        .unwrap()
        .then((r) => r.blob);
    },
    signFn(cmsSignature) {
      return signJobApp({ candidateId: candidateId!, cms_signature: cmsSignature }).unwrap();
    },
    skipFileUpload: true,
    onSuccess() {
      setPrompt({
        title: tCommon("signing.success.jobApplicationTitle"),
        text: tCommon("signing.success.jobApplicationText"),
        variant: "success",
      });
    },
    setPrompt,
    t: tCommon,
  });

  if (
    !jobApplicationProp &&
    !electronic &&
    !fetchedJobApplication?.signed_pdf_url &&
    !jobApplicationSignedPdfUrl &&
    !candidateStage &&
    !shouldFetch
  ) {
    return null;
  }

  const stage = (candidateStage || jobApplicationProp?.stage || fetchedJobApplication?.stage) as HiringStage | undefined;
  const signingStatus = jobApplicationProp?.signing_status ?? electronic?.signing_status ?? signingStatusOverride ?? null;
  const actionsConfig = stage
    ? getJobApplicationActions(stage, isCounterSigning ? undefined : signingStatus)
    : { showUploadButton: false, showDownloadButton: false, showReviewButtons: false, showSignButton: false };

  const pdfUrl =
    jobApplicationSignedPdfUrl ??
    jobApplicationProp?.signed_pdf_url ??
    (electronic ? electronic.signed_pdf_url : fetchedJobApplication?.signed_pdf_url ?? null);
  const originalFileUrl = jobApplicationProp?.original_file_url ?? (electronic ? electronic.original_file_url : null);
  const createdAt = jobApplicationProp?.created_at ?? electronic?.created_at ?? null;
  const uploadedAt =
    jobApplicationProp?.job_application_uploaded_at ?? fetchedJobApplication?.job_application_uploaded_at ?? null;
  const reviewedAt =
    jobApplicationProp?.job_application_reviewed_at ?? fetchedJobApplication?.job_application_reviewed_at ?? null;
  const reviewedByName = jobApplicationProp?.reviewed_by_name ?? fetchedJobApplication?.reviewed_by_name ?? null;
  const reviewNoteText =
    jobApplicationProp?.job_application_review_note ?? fetchedJobApplication?.job_application_review_note ?? null;
  const signingDocument = jobApplicationProp?.signing_document ?? null;
  const shouldShowPdf = !!pdfUrl;
  const shouldShowOriginal = !!originalFileUrl;

  const canSign =
    actionsConfig.showSignButton &&
    (isCounterSigning || (hasPermission(PERMISSIONS.HR.SIGN_CONTRACT) && hasRole("chief"))) &&
    candidateId !== null;

  interface FieldItem {
    key: string;
    label: string;
    value: string;
    icon: Icon;
    isStatus?: boolean;
    statusComponent?: ReactNode;
    isPdfFile?: boolean;
  }

  const fields: FieldItem[] = [
    {
      key: "candidateName",
      label: t("jobApplication.candidateName"),
      value: candidateName || "",
      icon: User,
    },
    {
      key: "createdAt",
      label: t("jobApplication.createdAt"),
      value: createdAt ? formatDateForDisplay(createdAt, false) : "",
      icon: Calendar,
    },
    {
      key: "status",
      label: t("jobApplication.statusLabel"),
      value: "",
      icon: DocumentText1,
      isStatus: true,
      statusComponent: <SigningStatus signingStatus={signingStatus} locale={locale} />,
    },
    {
      key: "uploadedAt",
      label: t("jobApplication.uploadedAt"),
      value: uploadedAt ? formatDateForDisplay(uploadedAt, false) : "",
      icon: Calendar,
    },
    {
      key: "reviewedAt",
      label: t("jobApplication.reviewedAt"),
      value: reviewedAt ? formatDateForDisplay(reviewedAt, false) : "",
      icon: Calendar,
    },
    {
      key: "reviewedBy",
      label: t("jobApplication.reviewedBy"),
      value: reviewedByName || "",
      icon: User,
    },
    {
      key: "reviewNote",
      label: t("jobApplication.reviewNote"),
      value: reviewNoteText || "",
      icon: MessageText1,
    },
    ...((shouldShowPdf || shouldShowOriginal)
      ? [
          {
            key: "applicationFile",
            label: t("jobApplication.applicationFileLabel"),
            value: "",
            icon: DocumentText1 as Icon,
            isPdfFile: true,
          },
        ]
      : []),
  ];

  const visibleFields = fields.filter((f) => f.value || f.isStatus || f.isPdfFile);

  const showSign = canSign;
  const showReview = !!(actionsConfig.showReviewButtons && onReview && hasPermission(PERMISSIONS.HR.CREATE_EMPLOYEE));
  const showRevoke =
    !!signingDocument &&
    (signingDocument.status === 0 || signingDocument.status === 1) &&
    user?.iin === signingDocument.org_signer_iin;
  const showNcaToast = stage === JOB_APP_PENDING && actionsConfig.showSignButton;

  function renderJobAppFooter() {
    let buttonCount = 0;
    if (showSign) buttonCount++;
    if (showReview) buttonCount += 3;
    if (showRevoke) buttonCount++;

    if (buttonCount === 0) return undefined;

    const buttonSize: "lg" | "huge" = buttonCount >= 4 ? "lg" : "huge";

    return (
      <div className="flex flex-col gap-3">
        {showNcaToast && (
          <Toast
            color="grey"
            text={tCommon("signing.ncalayerHint")}
            closable={false}
            autoClose={false}
            isFullWidth
          />
        )}
        {showSign && (
          <Button
            variant="primary"
            size={buttonSize}
            className="w-full"
            disabled={jobAppSigning.isLoading || isPreviewing}
            onClick={() => jobAppSigning.handleSign()}>
            <Edit size={16} color="currentColor" />
            {tCommon("signing.signJobApplication")}
          </Button>
        )}
        {showReview && (
          <>
            <Button
              variant="primary"
              size={buttonSize}
              className="w-full"
              disabled={isReviewing}
              onClick={() => setShowApproveModal(true)}>
              <CheckIcon />
              {t("jobApplication.actions.approve")}
            </Button>
            <Button
              variant="secondary"
              size={buttonSize}
              className="w-full"
              disabled={isReviewing}
              onClick={() => onReview!("revision")}>
              <Edit2 size={16} color="currentColor" />
              {t("jobApplication.actions.revision")}
            </Button>
            <Button
              variant="danger"
              size={buttonSize}
              className="w-full"
              disabled={isReviewing}
              onClick={() => onReview!("reject")}>
              <CloseIcon />
              {t("jobApplication.actions.reject")}
            </Button>
          </>
        )}
        {showRevoke && (
          <RevokeSigningButton
            size={buttonSize}
            documentId={signingDocument!.document_id}
            onSuccess={() => onSignSuccess?.()}
          />
        )}
      </div>
    );
  }

  function renderField(field: FieldItem, index: number, array: FieldItem[]) {
    const pdfPrimaryUrl = field.isPdfFile ? ((pdfUrl || originalFileUrl) as string) : undefined;
    const pdfSecondaryUrl = field.isPdfFile && pdfUrl && originalFileUrl ? originalFileUrl : undefined;
    return (
      <DetailFieldRow
        key={field.key}
        label={field.label}
        value={field.value}
        icon={field.icon}
        isStatus={field.isStatus}
        statusComponent={field.statusComponent}
        isPdfFile={field.isPdfFile}
        pdfPrimaryUrl={pdfPrimaryUrl}
        pdfSecondaryUrl={pdfSecondaryUrl}
        isLast={index === array.length - 1}
      />
    );
  }

  return (
    <>
      {jobAppSigning.isLoading && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={tCommon(`signing.phases.${jobAppSigning.phase}`)}
          additionalText={tCommon("signing.phases.pleaseWait")}
          namespace="Common"
        />
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant || "success"}
          onClose={() => {
            const wasSuccess = prompt.variant === "success";
            setPrompt(null);
            if (wasSuccess) onSignSuccess?.();
          }}
          namespace="ContractsPage"
        />
      )}

      <ModalForm
        icon={DocumentText1}
        onClose={onClose}
        resize={false}
        width="w-xl"
        footer={shouldFetch && isFetchingJobApp ? undefined : renderJobAppFooter()}>
        {shouldFetch && isFetchingJobApp ? (
          <div className="flex flex-col gap-6 h-full min-h-0">
            <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke shrink-0">
              <Skeleton height={28} width={240} />
            </div>
            <div className="flex-1">
              <div className="flex flex-col py-4">
                <div className="flex flex-col gap-3">
                  {Array.from({ length: 6 }).map(function (_, idx) {
                    return (
                      <div key={idx} className="py-3 border-b surface-base-stroke">
                        <Skeleton height={20} width="100%" />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <Skeleton height={44} width="100%" />
          </div>
        ) : (
          <div className="flex flex-col gap-6 h-full min-h-0">
            <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke shrink-0">
              <h4 className="text-display-2xs content-base-primary">{t("jobApplication.title")}</h4>
            </div>

            <div className="flex-1 overflow-auto min-h-0 p-1">
              <div className="flex flex-col py-4">
                <div className="flex flex-col">{visibleFields.map(renderField)}</div>
              </div>
            </div>
          </div>
        )}
      </ModalForm>

      {showApproveModal && (
        <ModalForm
          icon={CheckIcon}
          onClose={() => setShowApproveModal(false)}
          footer={
            <div className="grid grid-cols-[2fr_3fr] gap-3">
              <Button variant="secondary" size="huge" onClick={() => setShowApproveModal(false)}>
                {t("modals.cancel")}
              </Button>
              <Button
                variant="primary"
                size="huge"
                disabled={!approvalResolution || isReviewing}
                onClick={() => {
                  if (approvalResolution && onReview) {
                    onReview("approve", approvalResolution, reviewNote);
                    setShowApproveModal(false);
                    setApprovalResolution(null);
                    setReviewNote("");
                  }
                }}>
                {t("jobApplication.actions.approve")}
              </Button>
            </div>
          }>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke">
              <h4 className="text-display-2xs content-base-primary">
                {t("jobApplication.approveModal.title")}
              </h4>
            </div>

            <div className="flex flex-col gap-4">
              <Select
                label={t("jobApplication.approveModal.resolutionLabel")}
                placeholder={t("jobApplication.approveModal.resolutionPlaceholder")}
                value={approvalResolution}
                onChange={(value) => setApprovalResolution(value as "approved" | "supported" | "no_objection" | null)}
                options={[
                  {
                    label: t("jobApplication.approveModal.resolution.approved"),
                    value: "approved" as const,
                  },
                  {
                    label: t("jobApplication.approveModal.resolution.supported"),
                    value: "supported" as const,
                  },
                  {
                    label: t("jobApplication.approveModal.resolution.no_objection"),
                    value: "no_objection" as const,
                  },
                ]}
              />
            </div>
          </div>
        </ModalForm>
      )}
    </>
  );
}
